import { UserPlus } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'

import type { Role } from '@ankor-io/common/types/account'
import { AccountPreferenceKeys, OnboardingStatus } from '@ankor-io/common/types/account'
import { Button } from '@ankor-io/ui-components/src/Button'
import { Loading } from '@ankor-io/ui-components/src/Loading'

import { useCreateOrganizationInviteMutation } from '@/api/queries/useUsersQuery'
import { useUserContext } from '@/providers/user/useUserContext'
import InviteUsersModal from '@/views/components/InviteUserModal'

export const TeamSetup = () => {
  const { updatePreferences } = useUserContext()

  const [isLoadingForSkip, setIsLoadingForSkip] = useState(false)
  const [isInviteUsersModalOpen, setIsInviteUsersModalOpen] = useState(false)

  const createInviteMutation = useCreateOrganizationInviteMutation()

  const handleUserInvite = async (userData: { email: string; role: Role }) => {
    const invitePromise = async () => {
      const invite = await createInviteMutation.mutateAsync({
        email: userData.email,
        roles: [userData.role],
      })
      setIsInviteUsersModalOpen(false)
      return invite
    }

    toast.promise(invitePromise(), {
      loading: 'Inviting user...',
      success: 'User invited successfully',
      error: 'Failed to invite user',
    })
  }

  const handleSkipForNow = async () => {
    setIsLoadingForSkip(true)
    try {
      await updatePreferences({
        [AccountPreferenceKeys.ONBOARDING_STATUS]: OnboardingStatus.COMPLETED,
      })
    } catch (error) {
      console.error('Failed to skip for now', error)
    } finally {
      setIsLoadingForSkip(false)
    }
  }

  return (
    <>
      <div className="flex flex-col justify-between gap-y-6 sm:gap-y-8">
        <div className="space-y-3 sm:space-y-4">
          <h1 className="text-2xl sm:text-2xl font-bold text-gray-900 dark:text-gray-50">Invite Team members</h1>
          <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400">
            Invite your team so they can start using cards and logging expenses.
          </p>

          <div className="space-y-4 sm:space-y-6">
            <div className="space-y-3 sm:space-y-4">
              <Button
                color="primary"
                className="w-full"
                onClick={() => setIsInviteUsersModalOpen(true)}
              >
                <UserPlus data-slot="icon" />
                Invite Team Members
              </Button>

              {isLoadingForSkip ? (
                <div className="flex justify-center">
                  <Loading className="size-10" />
                </div>
              ) : (
                <Button
                  plain
                  className="w-full"
                  onClick={handleSkipForNow}
                  disabled={isLoadingForSkip}
                >
                  Skip for now
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Bottom spacer */}
        <div className="block" />
      </div>

      <InviteUsersModal
        isOpen={isInviteUsersModalOpen}
        onClose={() => setIsInviteUsersModalOpen(false)}
        onInvite={handleUserInvite}
        existingUsers={[]}
        existingInvites={[]}
      />
    </>
  )
}

export default TeamSetup
